import React, { useState } from "react";
import {
  SubjectType,
  SheetLength,
  buildRecallPrompt,
} from "../utils/RecallPromptBuilder";
import { exportRecallSheetToPDF } from "../utils/recallPdfExport";
import { RecallCollection, RecallItem, User } from "../types";
import { FaLock } from "react-icons/fa";
import { ShareButtons } from "./ShareButtons";
import "../styles_1/FlashcardGenerator.css";

interface RecallGeneratorProps {
  onSaveSheet?: (sheet: RecallCollection) => void;
  user: User | null;
  onShowAuth: () => void;
}

export const RecallGenerator: React.FC<RecallGeneratorProps> = ({
  onSaveSheet,
  user,
  onShowAuth
}) => {
  const [inputText, setInputText] = useState("");
  const [subject, setSubject] = useState<SubjectType>("POLITY");
  const [length, setLength] = useState<SheetLength>("1_PAGE");
  const [focusArea, setFocusArea] = useState("");
  const [jsonOutput, setJsonOutput] = useState("");
  const [generatedItems, setGeneratedItems] = useState<RecallItem[]>([]);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(1);

  const subjects: SubjectType[] = [
    "POLITY",
    "ECONOMY",
    "HISTORY_ANCIENT",
    "HISTORY_MEDIEVAL",
    "HISTORY_MODERN",
    "ART_CULTURE",
    "GEOGRAPHY",
    "ENVIRONMENT",
    "SCIENCE_TECH",
    "IR",
  ];


  const handleCopyPrompt = () => {
    if (!inputText.trim()) return alert("Please enter some notes first.");

    // 1. Build and Copy Prompt
    const prompt = buildRecallPrompt(inputText, subject, length, focusArea);
    navigator.clipboard.writeText(prompt);

    // 2. Show Clear Instructions (The Message Pop)
    alert(
      "✅ Prompt Copied to Clipboard!\n\n" +
      "1. ChatGPT will open automatically.\n" +
      "2. PASTE (Ctrl+V) this prompt there.\n" +
      "3. COPY the code block it generates.\n" +
      "4. Return here and PASTE it in Step 2."
    );

    // 3. Open AI
    window.open("https://chatgpt.com", "_blank");
  };

  const parseRecallJSON = (json: string): RecallItem[] => {
    try {
      let cleanJson = json
        .replace(/```json/g, "")
        .replace(/```/g, "")
        .trim();
      const parsed = JSON.parse(cleanJson);
      if (!Array.isArray(parsed)) throw new Error("Output is not an array");

      return parsed.map((item: any) => ({
        question: item.cue || item.question || "Unknown Question",
        answer: item.recall || item.answer || "No detail provided",
        cue: item.cue || "Unknown Cue",
        recall: item.recall || "No detail provided",
        category: item.category || "General",
      }));
    } catch (e) {
      console.error(e);
      throw new Error("Failed to parse JSON");
    }
  };

  const handleProcessJSON = () => {
    try {
      const items = parseRecallJSON(jsonOutput);
      setGeneratedItems(items);
      alert(`Parsed ${items.length} recall points!`);
    } catch (e) {
      alert("Invalid JSON. Please check the output.");
    }
  };

  const handleSaveClick = () => {
    if (!onSaveSheet) return;
    const newSheet: RecallCollection = {
      id: Date.now().toString(),
      name: `${subject} Recall - ${length}`,
      subject: subject,
      dateCreated: new Date().toISOString(),
      items: generatedItems,
      attempts: []
    };
    onSaveSheet(newSheet);
    alert("Saved to Profile!");
  };

  return (
    <div className="generator">
      <div className="generator-header" style={{ borderColor: "#8b5cf6" }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div>
            <h1 className="generator-title" style={{ color: "#8b5cf6" }}>
              Active Recall Builder
            </h1>
            <p className="generator-subtitle">
              Create concise, high-yield cheat sheets for revision.
            </p>
          </div>
          <button
            className="secondary-btn"
            onClick={() => { setShowTutorial(true); setTutorialStep(1); }}
            style={{ padding: '8px 16px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '20px' }}
          >
            <span style={{ marginRight: '8px' }}>💡</span> Tutorial
          </button>
        </div>
      </div>

      {/* 1. CONFIGURATION */}
      <div className="input-section">
        <div className="section-header">
          <h2>1. Configure Sheet</h2>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "20px",
            marginBottom: "20px",
          }}
        >
          <div>
            <label>Subject</label>
            <select
              className="select-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value as SubjectType)}
            >
              {subjects.map((s) => (
                <option key={s} value={s}>
                  {s.replace("_", " & ")}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>Target Length</label>
            <select
              className="select-input"
              value={length}
              onChange={(e) => setLength(e.target.value as SheetLength)}
            >
              <option value="1_PAGE">1 Page (Quick Revision)</option>
              <option value="2_PAGES">2 Pages (Standard)</option>
              <option value="3_PAGES">3 Pages (Comprehensive)</option>
            </select>
          </div>
        </div>

        <label>Focus Topic (Optional)</label>
        <input
          className="text-input"
          placeholder="e.g. Fundamental Rights, Mughal Architecture, Banking Reforms..."
          value={focusArea}
          onChange={(e) => setFocusArea(e.target.value)}
        />

        <label style={{ marginTop: "15px" }}>Paste Notes / Text</label>
        <textarea
          className="notes-input"
          rows={6}
          placeholder="Paste article text, book summaries, or raw notes here..."
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
        />

        <button
          className="generate-btn"
          style={{ background: "#8b5cf6" }}
          onClick={handleCopyPrompt}
        >
          🚀 Copy Prompt & Open AI
        </button>
      </div>

      {/* 2. OUTPUT */}
      <div className="input-section">
        <div className="section-header">
          <h2>2. Process Output</h2>
        </div>
        <textarea
          className="notes-input"
          rows={5}
          placeholder="Paste the JSON response from AI here..."
          value={jsonOutput}
          onChange={(e) => setJsonOutput(e.target.value)}
        />
        <button className="generate-btn" onClick={handleProcessJSON}>
          ⚡ Generate Preview
        </button>
      </div>

      {/* 3. PREVIEW & EXPORT */}
      {generatedItems.length > 0 && (
        <div className="results-section">
          <div
            className="section-header"
            style={{ justifyContent: "space-between", display: "flex" }}
          >
            <h2>Preview ({generatedItems.length} items)</h2>
            <div>
              {onSaveSheet && (
                <button
                  className="action-btn primary"
                  style={{ marginRight: "10px" }}
                  onClick={() => {
                    if (!user) return onShowAuth();
                    handleSaveClick();
                  }}
                >
                  💾 Save Sheet
                </button>
              )}
              <ShareButtons title={`Checkout my UPSC ${subject} Recall Sheet on ScoreSmart!`} />
              <button
                className="secondary-btn"
                onClick={() => {
                  if (!user) return onShowAuth();
                  exportRecallSheetToPDF(
                    generatedItems,
                    subject,
                    "My_Recall_Sheet",
                    length
                  );
                }}
              >
                📄 Download PDF ({length.replace("_", " ")})
              </button>
            </div>
          </div>

          <div
            style={{
              background: "white",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              overflow: "hidden",
              maxHeight: "400px",
              overflowY: "auto",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "0.9rem",
              }}
            >
              <thead
                style={{ background: "#f8fafc", position: "sticky", top: 0 }}
              >
                <tr>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      borderBottom: "2px solid #e2e8f0",
                      color: "#64748b",
                    }}
                  >
                    CUE
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      borderBottom: "2px solid #e2e8f0",
                      color: "#64748b",
                    }}
                  >
                    RECALL
                  </th>
                </tr>
              </thead>
              <tbody>
                {generatedItems.map((item, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td
                      style={{
                        padding: "10px 12px",
                        fontWeight: 600,
                        color: "#334155",
                        width: "40%",
                      }}
                    >
                      {item.cue || item.question}
                    </td>
                    <td style={{ padding: "10px 12px", color: "#475569" }}>
                      {item.recall || item.answer}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TUTORIAL OVERLAY */}
      {showTutorial && (
        <div className="tutorial-overlay">
          <div className="tutorial-content">
            <div className="tutorial-step-card">
              <h3>Step {tutorialStep} of 4</h3>
              <p>
                {tutorialStep === 1 && "Start by configuring your subject and pasting your study notes. High-yield text works best!"}
                {tutorialStep === 2 && "Click the Copy Prompt button. This copies an optimized UPSC prompt and opens AI for you."}
                {tutorialStep === 3 && "Paste the AI's JSON output back here to generate your active recall preview."}
                {tutorialStep === 4 && "IMPORTANT: Always export your data using the 'Export All Data' button in the sidebar after your session to save your progress locally!"}
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                <button className="secondary-btn" onClick={() => setShowTutorial(false)}>Close</button>
                {tutorialStep < 4 ? (
                  <button className="generate-btn" style={{ margin: 0 }} onClick={() => setTutorialStep(tutorialStep + 1)}>Next Step</button>
                ) : (
                  <button className="generate-btn" style={{ margin: 0, background: '#16a34a' }} onClick={() => setShowTutorial(false)}>Got It!</button>
                )}
              </div>
            </div>
            {/* Animated Arrows */}
            <div className={`tutorial-arrow step-${tutorialStep}`}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M12 5v14M5 12l7 7 7-7" />
              </svg>
            </div>
          </div>
        </div>
      )}
    </div >
  );
};